import { cn } from "@newxsystems/shared";
import type { AspectRatio, IImageComponentProps } from "@newxsystems/types";
import { getComponent } from "../../../../client";
import { HeroContent } from "../HeroContent/HeroContent";
import type { IHeroSplitImageProps } from "./HeroSplitImage.props";

const aspectRatioClasses: Record<AspectRatio, string> = {
  "1:1": "aspect-square",
  "4:3": "aspect-[4/3]",
  "3:2": "aspect-[3/2]",
  "16:9": "aspect-video",
  "21:9": "aspect-[21/9]",
  "3:4": "aspect-[3/4]",
  "2:3": "aspect-[2/3]",
};

const spacingClasses = {
  tight: "py-10 md:py-14",
  normal: "py-14 md:py-20",
  loose: "py-20 md:py-28",
};

export const HeroSplitImage = ({
  alignment,
  anchorId,
  buttons,
  description,
  eyebrow,
  heading,
  image,
  parentClass,
  spacing,
}: IHeroSplitImageProps) => {
  const Image = getComponent<IImageComponentProps>("Image");
  const hasContent = Boolean(eyebrow || heading || description || buttons?.length);
  const aspectRatio = image.aspectRatio ?? "16:9";

  if (!Image) {
    return null;
  }

  return (
    <section
      className={cn("w-full border-b border-foreground bg-background", parentClass)}
      id={anchorId ?? undefined}
    >
      <div className={cn("container", spacingClasses[spacing ?? "normal"])}>
        <div className="grid lg:grid-cols-12 lg:items-center lg:gap-6">
          <div
            className={cn(
              "relative order-1 min-w-0 overflow-hidden rounded-t-[14px] border border-foreground bg-foreground lg:order-2 lg:col-span-7 lg:rounded-[14px]",
              !hasContent && "rounded-[14px] lg:col-span-12",
              aspectRatioClasses[aspectRatio],
            )}
          >
            <Image
              alt={image.image.alternateText}
              className="absolute inset-0 max-w-none"
              decoding="async"
              imageClassName="object-cover"
              loading="eager"
              priority
              sizes={image.image.sizes ?? undefined}
              src={image.image.defaultSrc}
              srcSet={image.image.srcSet ?? undefined}
            />
          </div>
          {hasContent ? (
            <div className="order-2 flex min-w-0 items-center rounded-b-[14px] border-x border-b border-foreground bg-field-lilac px-6 py-12 md:px-10 md:py-14 lg:order-1 lg:col-span-5 lg:rounded-[14px] lg:border lg:px-12">
              <HeroContent
                alignment={alignment}
                buttons={buttons}
                compact
                description={description}
                eyebrow={eyebrow}
                heading={heading}
              />
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
};

HeroSplitImage.displayName = "Hero::HeroSplitImage";
